// @ts-nocheck
/**
 * Estimation price calculator (material-only).
 * Used on the estimation page and anywhere else we need the same logic.
 * Prices are per unit: $/foot (length), $/step (stairs), $/door.
 * Heights in form: 36", 42", 48" (mapped to H34, H40, H46 in pricing rules).
 */
(function (global) {
  "use strict";

  // ——— Unit prices (CAD $) ———
  // Rampes en verre — length = $/foot (longueur estimée excl. escalier), stairs = $/step, door = $/door
  var GLASS = {
    lengthPerFoot: { 36: 86, 42: 92, 48: 98 },
    stairsPerStep: { 36: 153, 42: 171, 48: 188 },
    doorPerDoor: { 36: 262, 42: 244, 48: 244 },
  };

  // Rampes en aluminium — distinct tables per type + main courante
  // Rampe aluminium avec barreaux (main courante simple)
  var ALU_BALUSTER_SIMPLE = {
    lengthPerFoot: { 36: 92, 42: 99, 48: 105 },
    stairsPerStep: { 36: 105, 42: 105, 48: 105 },
    doorPerDoor: { 36: 515, 42: 515, 48: 515 },
  };

  // Rampe aluminium avec barreaux (main courante double)
  var ALU_BALUSTER_DOUBLE = {
    lengthPerFoot: { 36: 105, 42: 110, 48: 115 },
    stairsPerStep: { 36: 115, 42: 115, 48: 115 },
    doorPerDoor: { 36: 515, 42: 515, 48: 515 },
  };

  // Rampe en verre 6mm trempé (main courante simple)
  var ALU_GLASS6_SIMPLE = {
    lengthPerFoot: { 36: 97, 42: 108, 48: 119 },
    stairsPerStep: { 36: 119, 42: 119, 48: 119 },
    doorPerDoor: { 36: 515, 42: 515, 48: 515 },
  };

  // Rampe en verre 6mm trempé (main courante double)
  var ALU_GLASS6_DOUBLE = {
    lengthPerFoot: { 36: 111, 42: 122, 48: 133 },
    stairsPerStep: { 36: 133, 42: 133, 48: 133 },
    doorPerDoor: { 36: 515, 42: 515, 48: 515 },
  };

  // Rampe ornementale — only H46 is supported; others are treated as n/a (0)
  var ALU_ORNAMENTAL = {
    lengthPerFoot: { 36: 0, 42: 0, 48: 50 },
    stairsPerStep: { 36: 0, 42: 0, 48: 0 },
    doorPerDoor: { 36: 0, 42: 0, 48: 598 },
  };

  function getAluminumTable(aluminumType, mainHandrail) {
    var type = aluminumType || "";
    var handrail = (mainHandrail || "").toLowerCase();
    if (type === "ornementale") return ALU_ORNAMENTAL;
    if (type === "aluminum_glass_6mm") {
      return handrail === "double" ? ALU_GLASS6_DOUBLE : ALU_GLASS6_SIMPLE;
    }
    // Default: aluminum with balusters
    return handrail === "double" ? ALU_BALUSTER_DOUBLE : ALU_BALUSTER_SIMPLE;
  }

  /**
   * @param {Object} config - Estimation input (matches _collectEstimateInput from cfg-wizard).
   * @param {string} config.railingType - "glass" | "aluminum"
   * @param {string} [config.aluminumType] - "aluminum_baluster" | "aluminum_glass_6mm" | "ornementale"
   * @param {string} [config.mainHandrail] - "simple" | "double" (ignored for ornementale)
   * @param {number} config.heightIn - 36 | 42 | 48
   * @param {number} config.totalLengthFt - Total length in feet (excluding stairs)
   * @param {Object} config.doors - { hasDoors, count }
   * @param {Object} config.stairs - { hasStairs, steps, type? } — type "double" = double the stairs price
   * @returns {{ total: number, formatted: string, breakdown?: Object }}
   */
  function calculate(config) {
    var total = 0;
    var breakdown = { length: 0, stairs: 0, doors: 0 };

    var isAluminum = config && config.railingType === "aluminum";
    var aluminumType = isAluminum && config ? config.aluminumType : "";
    var mainHandrail = isAluminum && config ? config.mainHandrail : "";
    var table = isAluminum ? getAluminumTable(aluminumType, mainHandrail) : GLASS;
    var height = config && Number(config.heightIn);
    if (!height || (height !== 36 && height !== 42 && height !== 48)) {
      height = 42;
    }

    var lengthFt = config && typeof config.totalLengthFt === "number" && config.totalLengthFt >= 0 ? config.totalLengthFt : 0;
    var lengthPrice = (table.lengthPerFoot[height] || 0) * lengthFt;
    total += lengthPrice;
    breakdown.length = lengthPrice;

    var steps = 0;
    var stairsDouble = false;
    if (config && config.stairs && config.stairs.hasStairs && typeof config.stairs.steps === "number" && config.stairs.steps > 0) {
      steps = config.stairs.steps;
      stairsDouble = config.stairs.type === "double";
    }
    var stairsPrice = (table.stairsPerStep[height] || 0) * steps * (stairsDouble ? 2 : 1);
    total += stairsPrice;
    breakdown.stairs = stairsPrice;

    var doorsCount = 0;
    if (config && config.doors && config.doors.hasDoors && typeof config.doors.count === "number" && config.doors.count > 0) {
      doorsCount = config.doors.count;
    }
    var doorsPrice = (table.doorPerDoor[height] || 0) * doorsCount;
    total += doorsPrice;
    breakdown.doors = doorsPrice;

    var formatted = formatPrice(total);
    return { total: total, formatted: formatted, breakdown: breakdown };
  }

  function formatPrice(num) {
    if (!Number.isFinite(num) || num < 0) return "0";
    var rounded = Math.round(num);
    return rounded.toLocaleString("fr-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  /**
   * Stairs-only total for a list of stair sections (e.g. configurator or product page).
   * @param {Array<{ steps: number, type?: string }>} stairsArray
   * @param {string|{ railingType?: string, aluminumType?: string, mainHandrail?: string }} railingTypeOrOptions
   *        - "glass" | "aluminum" (backward compatible)
   *        - or options object to select a specific aluminum table
   * @param {number} heightIn - 36 | 42 | 48
   * @returns {{ total: number, formatted: string }}
   */
  function calculateStairsOnly(stairsArray, railingTypeOrOptions, heightIn) {
    var opts = {};
    if (typeof railingTypeOrOptions === "string" || railingTypeOrOptions == null) {
      opts.railingType = railingTypeOrOptions;
    } else if (typeof railingTypeOrOptions === "object") {
      opts = railingTypeOrOptions;
    }
    var isAluminum = opts.railingType === "aluminum";
    var table = isAluminum ? getAluminumTable(opts.aluminumType, opts.mainHandrail) : GLASS;
    var height = Number(heightIn);
    if (!height || (height !== 36 && height !== 42 && height !== 48)) height = 42;
    var total = 0;
    if (Array.isArray(stairsArray)) {
      for (var i = 0; i < stairsArray.length; i++) {
        var s = stairsArray[i];
        var steps = (s && typeof s.steps === "number" && s.steps >= 1) ? s.steps : 0;
        var mult = (s && s.type === "double") ? 2 : 1;
        total += (table.stairsPerStep[height] || 0) * steps * mult;
      }
    }
    return { total: total, formatted: formatPrice(total) };
  }

  global.RBEstimationPrice = {
    calculate: calculate,
    calculateStairsOnly: calculateStairsOnly,
    formatPrice: formatPrice,
    GLASS: GLASS,
    // Expose aluminum tables for debugging if needed
    ALU_BALUSTER_SIMPLE: ALU_BALUSTER_SIMPLE,
    ALU_BALUSTER_DOUBLE: ALU_BALUSTER_DOUBLE,
    ALU_GLASS6_SIMPLE: ALU_GLASS6_SIMPLE,
    ALU_GLASS6_DOUBLE: ALU_GLASS6_DOUBLE,
    ALU_ORNAMENTAL: ALU_ORNAMENTAL,
  };
})(typeof window !== "undefined" ? window : this);
